import { Component } from 'preact';
import { ChevronLeft } from 'lucide-react';
import { GOLD, ACCENT_GOLD, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED } from '../../lib/medievalTheme';
import { getSoundSettings, saveSoundSettings } from '../../utils/arena/soundSettings';
import { updateMusicVolume } from '../../utils/arena/musicManager';
import { updateCustomMusicVolume } from '../../utils/arena/customMusicManager';
import { updateSubsurfaceAmbienceVolume } from '../../utils/arena/subsurfaceAmbience';
import {
  getAmbienceState,
  subscribeAmbience,
  setAmbienceVolume,
  playAmbience,
  pauseAmbience,
  updateAmbienceVolume,
} from '../../utils/arena/ambienceManager';
import { playUI, UI } from '../../utils/arena/uiSounds';
import { t } from '../../localization/t';

// Phone settings. Full-screen overlay over the mobile hub: back button
// up top, then a natively scrolling stack of sections. Every control
// writes through the same utils the desktop SettingsScreen uses
// (soundSettings + ambienceManager, both backed by persistentPrefs), so
// a change made on a phone shows up on desktop and vice versa.
//
// Mobile-appropriate scope on purpose. Desktop-only settings are ABSENT,
// not stubbed:
//   - Display (render scale, window mode, aspect, brightness): the phone
//     owns its own screen and its OS owns the brightness. Nothing here
//     would move a pixel a player cares about.
//   - Controls (keybindings): no keyboard.
//   - Discord Rich Presence + Updates: both live in the Electron main
//     process, which does not exist on a phone.
//   - Master volume + custom music library: the hardware volume keys are
//     the master fader, and the library needs a native folder picker.
// Desktop keeps all of those.

const PANEL = {
  border: `1px solid ${GOLD} 0.22)`,
  borderRadius: 'var(--radius-xl)',
  background: 'linear-gradient(180deg, rgba(30,24,14,0.9) 0%, rgba(18,13,8,0.94) 100%)',
  boxShadow: 'inset 0 1px 0 rgba(255,230,170,0.06), 0 2px 10px rgba(0,0,0,0.45)',
  overflow: 'hidden',
};

const ROW_BORDER = { borderTop: `1px solid ${GOLD} 0.12)` };

// The themed .vh-slider / .vh-toggle in index.css are sized in cqw,
// which inside the phone canvas would render a ~8px thumb. These are the
// same brass-and-groove recipe re-cut in rems, with a thumb big enough
// to grab with a thumb. Scoped by class name; phone screens are the only
// place this markup exists.
const SLIDER_CSS = `
.vk-msettings-slider {
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 2.75rem;
  background: transparent;
  outline: none;
  margin: 0;
  padding: 0;
  touch-action: pan-y;
}
.vk-msettings-slider::-webkit-slider-runnable-track {
  height: 0.5rem;
  border-radius: var(--radius-sm);
  background: linear-gradient(90deg,
    rgba(232,195,110,0.92) 0%,
    rgba(180,140,50,0.85) var(--vk-fill, 50%),
    rgba(0,0,0,0.55) var(--vk-fill, 50%),
    rgba(0,0,0,0.65) 100%);
  box-shadow: inset 0 1px 2px rgba(0,0,0,0.7), 0 0 0 1px rgba(0,0,0,0.55);
}
.vk-msettings-slider::-moz-range-track {
  height: 0.5rem;
  border-radius: var(--radius-sm);
  background: linear-gradient(90deg,
    rgba(232,195,110,0.92) 0%,
    rgba(180,140,50,0.85) var(--vk-fill, 50%),
    rgba(0,0,0,0.55) var(--vk-fill, 50%),
    rgba(0,0,0,0.65) 100%);
  box-shadow: inset 0 1px 2px rgba(0,0,0,0.7), 0 0 0 1px rgba(0,0,0,0.55);
}
.vk-msettings-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 1.75rem;
  height: 1.75rem;
  margin-top: -0.625rem;
  border-radius: 50%;
  background: radial-gradient(circle at 35% 28%, #f6dc98 0%, #d4a843 48%, #8a6a22 100%);
  border: 1px solid rgba(35,24,8,0.85);
  box-shadow: inset 0 1px 1px rgba(255,255,255,0.38), 0 1px 3px rgba(0,0,0,0.7);
}
.vk-msettings-slider::-moz-range-thumb {
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  background: radial-gradient(circle at 35% 28%, #f6dc98 0%, #d4a843 48%, #8a6a22 100%);
  border: 1px solid rgba(35,24,8,0.85);
  box-shadow: inset 0 1px 1px rgba(255,255,255,0.38), 0 1px 3px rgba(0,0,0,0.7);
}
.vk-msettings-slider:active::-webkit-slider-thumb { transform: scale(1.08); }
.vk-msettings-slider:disabled::-webkit-slider-runnable-track {
  background: linear-gradient(90deg,
    rgba(120,100,50,0.28) 0%,
    rgba(80,70,40,0.3) var(--vk-fill, 50%),
    rgba(0,0,0,0.45) var(--vk-fill, 50%),
    rgba(0,0,0,0.55) 100%);
}
.vk-msettings-slider:disabled::-webkit-slider-thumb {
  background: radial-gradient(circle at 35% 28%, rgba(150,130,80,0.5) 0%, rgba(80,65,30,0.55) 100%);
  box-shadow: none;
}
`;

function SectionHeader({ children }) {
  return (
    <div className="flex items-center gap-2 mt-5 mb-2">
      <div className="flex-1 h-px" style={{ background: `linear-gradient(90deg, transparent 0%, ${GOLD} 0.22) 100%)` }} />
      <div className="arena-heading text-xs font-semibold" style={{ color: `${GOLD} 0.75)`, letterSpacing: '0.08em' }}>
        {children}
      </div>
      <div className="flex-1 h-px" style={{ background: `linear-gradient(90deg, ${GOLD} 0.22) 0%, transparent 100%)` }} />
    </div>
  );
}

// Track-and-knob switch. The button itself is the tap target (2.75rem
// tall); the visible track is smaller and centered inside it.
function Toggle({ on, label, onToggle }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      className="flex items-center justify-center shrink-0 active:scale-[0.94] transition-transform duration-100"
      style={{ width: '3.75rem', height: '2.75rem' }}
      onClick={() => { playUI(UI.CLICK); onToggle(); }}
    >
      <span
        className="relative block"
        style={{
          width: '3rem',
          height: '1.75rem',
          borderRadius: 'var(--radius-full)',
          background: on
            ? 'linear-gradient(180deg, rgba(120,92,34,0.95) 0%, rgba(80,60,20,0.95) 100%)'
            : 'linear-gradient(180deg, rgba(24,20,14,0.95) 0%, rgba(12,10,7,0.95) 100%)',
          border: on ? `1px solid ${GOLD} 0.65)` : '1px solid rgba(255,255,255,0.12)',
          boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.6)',
          transition: 'background 160ms ease, border-color 160ms ease',
        }}
      >
        <span
          className="absolute"
          style={{
            top: '0.1875rem',
            left: on ? '1.4375rem' : '0.1875rem',
            width: '1.25rem',
            height: '1.25rem',
            borderRadius: '50%',
            background: on
              ? 'radial-gradient(circle at 35% 28%, #f6dc98 0%, #d4a843 48%, #8a6a22 100%)'
              : 'radial-gradient(circle at 35% 28%, #6b665f 0%, #3a3733 100%)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.7)',
            transition: 'left 160ms ease, background 160ms ease',
          }}
        />
      </span>
    </button>
  );
}

// One audio channel: name + switch on the first line, slider + percent
// on the second. Two lines because a landscape phone has the height to
// spare and a full-width slider is far easier to aim at than a stubby one.
function ChannelRow({ label, enabled, value, onToggle, onChange, first }) {
  const pct = Math.round(value * 100);
  return (
    <div className="px-4 py-2" style={first ? undefined : ROW_BORDER}>
      <div className="flex items-center justify-between gap-3">
        <span className="text-base font-semibold" style={{ color: TEXT_PRIMARY }}>{label}</span>
        <Toggle on={enabled} label={label} onToggle={onToggle} />
      </div>
      <div className="flex items-center gap-3">
        <input
          type="range"
          className="vk-msettings-slider"
          min={0}
          max={100}
          step={1}
          value={pct}
          disabled={!enabled}
          aria-label={t('{label} volume', { label })}
          style={{ '--vk-fill': `${pct}%`, opacity: enabled ? 1 : 0.5 }}
          onInput={(e) => onChange(parseInt(e.target.value, 10) / 100)}
        />
        <span className="text-sm tabular-nums text-right shrink-0" style={{ width: '2.75rem', color: TEXT_MUTED }}>
          {pct}%
        </span>
      </div>
    </div>
  );
}

export default class MobileSettings extends Component {
  state = {
    soundSettings: getSoundSettings(),
    ambience: getAmbienceState(),
    confirmingLogout: false,
  };

  componentDidMount() {
    // Ambience play state also flips from the game board and the
    // subsurface view, so mirror it rather than owning it.
    this.unsubAmbience = subscribeAmbience(() => {
      this.setState({ ambience: getAmbienceState() });
    });
  }

  componentWillUnmount() {
    this.unsubAmbience?.();
  }

  // Same write path as SettingsScreen.updateSound: persist, then poke
  // every element that reads the effective volume so the change is
  // audible while the finger is still on the slider.
  updateSound = (key, value) => {
    const next = { ...this.state.soundSettings, [key]: value };
    saveSoundSettings(next);
    this.setState({ soundSettings: next });
    updateMusicVolume();
    updateCustomMusicVolume();
    updateAmbienceVolume();
    updateSubsurfaceAmbienceVolume();
  };

  setAmbienceLevel = (value) => {
    setAmbienceVolume(value);
    this.setState({ ambience: getAmbienceState() });
  };

  toggleAmbiencePlaying = () => {
    if (this.state.ambience.isPlaying) pauseAmbience();
    else playAmbience();
    this.setState({ ambience: getAmbienceState() });
  };

  handleBack = () => {
    playUI(UI.CANCEL);
    this.props.onClose?.();
  };

  handleLogout = () => {
    playUI(UI.CANCEL);
    this.props.onLogout?.();
  };

  render() {
    const ss = this.state.soundSettings;
    const amb = this.state.ambience;
    const { profile, version } = this.props;
    const { confirmingLogout } = this.state;

    return (
      <div className="absolute inset-0 z-30 flex flex-col phone-safe-pad" style={{ background: 'rgba(8,6,4,0.97)' }}>
        <style>{SLIDER_CSS}</style>

        {/* Header */}
        <div className="flex items-center gap-2 px-2 pt-2 pb-1.5" style={{ borderBottom: `1px solid ${GOLD} 0.18)` }}>
          <button
            type="button"
            aria-label={t('Back')}
            className="flex items-center justify-center shrink-0 active:scale-[0.92] transition-transform duration-100"
            style={{ width: '2.75rem', height: '2.75rem' }}
            onClick={this.handleBack}
          >
            <ChevronLeft size={24} color={TEXT_PRIMARY} />
          </button>
          <span className="arena-heading text-lg font-bold" style={{ color: TEXT_PRIMARY }}>
            {t('Settings')}
          </span>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 pb-6" style={{ overscrollBehavior: 'contain' }}>
          <SectionHeader>{t('Sound')}</SectionHeader>
          <div style={PANEL}>
            <ChannelRow
              first
              label={t('Music')}
              enabled={ss.musicEnabled}
              value={ss.musicVolume}
              onToggle={() => this.updateSound('musicEnabled', !ss.musicEnabled)}
              onChange={(v) => this.updateSound('musicVolume', v)}
            />
            <ChannelRow
              label={t('Sound Effects')}
              enabled={ss.sfxEnabled}
              value={ss.sfxVolume}
              onToggle={() => this.updateSound('sfxEnabled', !ss.sfxEnabled)}
              onChange={(v) => this.updateSound('sfxVolume', v)}
            />
            {/* Ambience has no enabled flag of its own: the switch is
                play/pause on the bed, which is what it persists. */}
            <ChannelRow
              label={t('Ambience')}
              enabled={amb.isPlaying}
              value={amb.volume}
              onToggle={this.toggleAmbiencePlaying}
              onChange={this.setAmbienceLevel}
            />
          </div>

          <SectionHeader>{t('Account')}</SectionHeader>
          <div style={PANEL}>
            <div className="flex items-center justify-between gap-3 px-4" style={{ minHeight: '3.25rem' }}>
              <span className="text-sm" style={{ color: TEXT_BODY }}>{t('Signed in as')}</span>
              <span className="text-base font-semibold truncate" style={{ color: ACCENT_GOLD }}>
                {profile?.name || t('Adventurer')}
              </span>
            </div>
            <div className="px-4 py-3" style={ROW_BORDER}>
              {confirmingLogout ? (
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    className="flex-1 text-sm font-semibold active:translate-y-px transition-transform duration-100"
                    style={{
                      minHeight: '2.75rem',
                      borderRadius: 'var(--radius-lg)',
                      border: '1px solid rgba(255,255,255,0.14)',
                      background: 'linear-gradient(180deg, rgba(28,23,15,0.95) 0%, rgba(16,12,8,0.95) 100%)',
                      color: TEXT_BODY,
                    }}
                    onClick={() => { playUI(UI.CANCEL); this.setState({ confirmingLogout: false }); }}
                  >
                    {t('Cancel')}
                  </button>
                  <button
                    type="button"
                    className="flex-1 text-sm font-bold active:translate-y-px transition-transform duration-100"
                    style={{
                      minHeight: '2.75rem',
                      borderRadius: 'var(--radius-lg)',
                      border: '1px solid rgba(180,60,60,0.55)',
                      background: 'linear-gradient(180deg, rgba(120,38,38,0.55) 0%, rgba(70,20,20,0.6) 100%)',
                      color: '#e59b9b',
                      textShadow: '0 1px 2px rgba(0,0,0,0.6)',
                    }}
                    onClick={this.handleLogout}
                  >
                    {t('Sign Out')}
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="w-full text-base font-bold active:translate-y-px transition-transform duration-100"
                  style={{
                    minHeight: '2.75rem',
                    borderRadius: 'var(--radius-lg)',
                    border: '1px solid rgba(180,60,60,0.35)',
                    background: 'linear-gradient(180deg, rgba(180,60,60,0.15) 0%, rgba(120,30,30,0.1) 100%)',
                    color: '#c45050',
                    textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                  }}
                  onClick={() => { playUI(UI.CLICK); this.setState({ confirmingLogout: true }); }}
                >
                  {t('Sign Out')}
                </button>
              )}
            </div>
          </div>

          <SectionHeader>{t('About')}</SectionHeader>
          <div style={PANEL}>
            <div className="flex items-center justify-between gap-3 px-4" style={{ minHeight: '3.25rem' }}>
              <span className="text-sm" style={{ color: TEXT_BODY }}>{t('Version')}</span>
              <span className="text-base font-semibold tabular-nums" style={{ color: TEXT_PRIMARY }}>
                {version ? `v${version}` : t('Unknown')}
              </span>
            </div>
            <div className="flex items-center justify-between gap-3 px-4" style={{ ...ROW_BORDER, minHeight: '3.25rem' }}>
              <span className="text-sm" style={{ color: TEXT_BODY }}>{t('Build')}</span>
              <span className="arena-heading text-base font-bold" style={{ color: `${GOLD} 0.85)` }}>
                {t('Open Beta')}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
